import { useEffect, useState } from "react";
import { http } from "../lib/api";
import { Loader2, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { track } from "../lib/analytics";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const priceStr = (cents) => `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`;

export const Shop = () => {
  const [data, setData] = useState({ products: [], skus: [] });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [selectedSku, setSelectedSku] = useState({});

  useEffect(() => {
    http.get("/shop/products")
      .then((r) => {
        setData(r.data);
        // default each product to the first SKU
        const defaults = {};
        r.data.products.forEach((p) => { defaults[p.id] = r.data.skus[0]?.sku; });
        setSelectedSku(defaults);
      })
      .catch(() => toast.error("couldn't load shop"))
      .finally(() => setLoading(false));
  }, []);

  const buy = async (product) => {
    const sku = selectedSku[product.id] || data.skus[0]?.sku;
    setBusyId(product.id + "-" + sku);
    track("shop_checkout_started", { product_id: product.id, sku });
    // Open sync so browsers accept it as a user gesture (Stripe blocks iframes).
    const stripeTab = window.open("about:blank", "_blank");
    if (stripeTab) {
      try { stripeTab.document.write("<title>Opening Stripe...</title><body style='background:#0a0a0a;color:#0f8;font-family:monospace;padding:2rem;'>Opening Stripe Checkout...</body>"); } catch (_) {}
    }
    try {
      const r = await http.post("/shop/checkout", {
        product_id: product.id,
        sku,
        quantity: 1,
        success_url: window.location.origin + "/shop/success",
        cancel_url: window.location.origin + "/shop",
      });
      if (stripeTab && !stripeTab.closed) {
        stripeTab.location.href = r.data.session_url;
      } else {
        try { window.top.location.href = r.data.session_url; }
        catch (_) { window.location.href = r.data.session_url; }
      }
      setBusyId(null);
    } catch (e) {
      if (stripeTab) stripeTab.close();
      toast.error("checkout failed: " + (e.response?.data?.detail || e.message));
      setBusyId(null);
    }
  };

  return (
    <div data-testid="shop-page" className="mx-auto max-w-7xl px-6 py-16">
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-matrix)] mb-3">// module.shop // vol. 1</div>
        <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight">the drop</h1>
        <p className="text-neutral-400 mt-4 max-w-2xl">
          Merch capsule. Hand-drawn illustrations, printed on-demand. Every purchase funds the ops and keeps the site free.
        </p>
      </div>

      {loading && <div className="text-neutral-500 text-sm" data-testid="shop-loading">loading drop...</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {data.products.map((p) => {
          const sku = data.skus.find((s) => s.sku === selectedSku[p.id]) || data.skus[0];
          return (
            <div
              key={p.id}
              data-testid={`shop-product-${p.id}`}
              className="bg-[#0a0a0a] hover:bg-[#111] p-5 flex flex-col gap-4 duration-150"
              style={{ transitionProperty: "background-color" }}
            >
              <div className="aspect-square overflow-hidden border border-white/10 bg-black">
                <img
                  src={BACKEND + p.image}
                  alt={p.name}
                  loading="lazy"
                  className="w-full h-full object-contain"
                  onError={(e) => { e.target.src = p.image; }}
                />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[color:var(--accent-amber)] mb-1">
                  {p.tagline}
                </div>
                <div className="font-display font-bold text-lg leading-tight">{p.name}</div>
              </div>
              <select
                data-testid={`shop-sku-${p.id}`}
                value={selectedSku[p.id] || ""}
                onChange={(e) => setSelectedSku({ ...selectedSku, [p.id]: e.target.value })}
                className="bg-black border border-white/15 text-white px-2 py-2 text-xs"
              >
                {data.skus.map((s) => (
                  <option key={s.sku} value={s.sku}>
                    {s.label} — {priceStr(s.price_cents)}
                  </option>
                ))}
              </select>
              <button
                data-testid={`shop-buy-${p.id}`}
                onClick={() => buy(p)}
                disabled={busyId?.startsWith(p.id + "-")}
                className="mt-auto inline-flex items-center justify-center gap-2 bg-[color:var(--accent-amber)] text-black px-3 py-3 text-xs uppercase tracking-widest font-bold hover:bg-white duration-150 disabled:opacity-50"
                style={{ transitionProperty: "background-color" }}
              >
                {busyId?.startsWith(p.id + "-") ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShoppingBag className="w-4 h-4" />}
                buy · {priceStr(sku.price_cents)}
              </button>
            </div>
          );
        })}
      </div>

      <div className="mt-8 text-[10px] uppercase tracking-widest text-neutral-500 text-center">
        checkout via stripe · fulfillment via printful (coming soon — pre-orders open)
      </div>
    </div>
  );
};
